/**
 * Serviço de componentes de produto (ingredientes / fórmula / peças)
 * Usa a tabela asset_components + RPC add_asset_component (migração 073).
 * Colunas reais: parent_asset_id, component_asset_id, quantity, uom.
 */

import { supabase } from '@/lib/supabase'

export interface ProductComponent {
  id: string
  unit_id: string
  parent_id: string       // parent_asset_id
  component_id: string    // component_asset_id
  qty: number             // quantity
  uom: string
  component_name?: string
  component_uom?: string
  component_price?: number
}

export async function listProductComponents(productId: string): Promise<ProductComponent[]> {
  const { data, error } = await supabase
    .from('asset_components')
    .select(`
      id,
      unit_id,
      parent_asset_id,
      component_asset_id,
      quantity,
      uom,
      component:component_asset_id (name, uom, price)
    `)
    .eq('parent_asset_id', productId)
    .order('created_at', { ascending: true })

  if (error) throw error

  return (data || []).map((row: any) => ({
    id: row.id,
    unit_id: row.unit_id,
    parent_id: row.parent_asset_id,
    component_id: row.component_asset_id,
    qty: row.quantity,
    uom: row.uom,
    component_name: row.component?.name,
    component_uom: row.component?.uom,
    component_price: row.component?.price
  }))
}

/**
 * Adiciona um componente usando o RPC add_asset_component.
 * source_type: 'asset' para insumos reais, 'template' para templates não materializados.
 * O RPC cria o asset automaticamente se source_type = 'template'.
 * 
 * baseQty: stock consumption qty converted to the asset's base UOM (e.g. 0.12 for "1 cup" = 0.12 kg)
 * baseUom: asset's base UOM from the asset record
 */
export async function addProductComponent(
  unitId: string,
  productId: string,
  sourceId: string,
  sourceType: 'asset' | 'template',
  qty: number,
  uom: string,
  baseQty?: number,  // stock consumption qty in asset's base UOM
  baseUom?: string   // asset's base UOM from the asset record
): Promise<ProductComponent> {
  const { data, error } = await supabase.rpc('add_asset_component', {
    p_unit_id:         unitId,
    p_parent_asset_id: productId,
    p_source_type:     sourceType,
    p_source_id:       sourceId,
    p_quantity:        qty,
    p_uom:             uom,
    p_base_qty:        baseQty,
    p_base_uom:        baseUom
  })

  if (error) throw error

  // RPC retorna { component_id, asset_id, ... } — buscamos o row completo
  const componentId: string = (data as any).component_id

  const { data: row, error: rowError } = await supabase
    .from('asset_components')
    .select(`
      id, unit_id, parent_asset_id, component_asset_id, quantity, uom,
      component:component_asset_id (name, uom, price)
    `)
    .eq('id', componentId)
    .single()

  if (rowError) throw rowError

  return {
    id: row.id,
    unit_id: row.unit_id,
    parent_id: row.parent_asset_id,
    component_id: row.component_asset_id,
    qty: row.quantity,
    uom: row.uom,
    component_name: (row as any).component?.name,
    component_uom: (row as any).component?.uom,
    component_price: (row as any).component?.price
  }
}

export async function updateProductComponent(
  id: string,
  qty: number,
  uom: string
): Promise<void> {
  const { error } = await supabase
    .from('asset_components')
    .update({ quantity: qty, uom })
    .eq('id', id)

  if (error) throw error
}

export async function removeProductComponent(id: string): Promise<void> {
  const { error } = await supabase
    .from('asset_components')
    .delete()
    .eq('id', id)

  if (error) throw error
}
